import { useConversationChat } from '@/api/hooks/useConversation';
import { useFollowUpQuestions } from '@/api/hooks/useUser';
import { useGoogleSuggestions } from '@/hooks/useGoogleSuggestions';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

// Types for the hooks parameters
type AskInputSuggestionParams = {
	onCloseModal?: () => void;
	followUpConversationId?: string | null;
	conversationId?: string | null;
};

export const useAskInputSuggestion = () => {
	const [question, setQuestion] = useState('');
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [isShowButton, setIsShowButton] = useState(true);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const { mutate: askNow, isPending: isAskNowLoading } = useConversationChat();
	const router = useRouter();

	// google suggestions
	const { suggestions: googleSuggestions } = useGoogleSuggestions(question);

	// follow up questions
	const {
		data: followUpQuestions,
		isFetching: isFollowUpLoading,
		error,
		refetch,
	} = useFollowUpQuestions();

	const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value.trim() === '') {
			setIsShowButton(true);
		} else {
			setIsShowButton(false);
		}
		setQuestion(e.target.value);
	}, []);

	const handleGetSuggestions = useCallback(() => {
		if (question.trim() === '' && !followUpQuestions) {
			refetch();
		}
		setShowSuggestions(true);
		setIsShowButton(false);
	}, [question, refetch, followUpQuestions]);

	const handleSelectSuggestion = useCallback((suggestion: string) => {
		setQuestion(suggestion);
	}, []);

	const handleAskNow = useCallback(
		({
			onCloseModal,
			followUpConversationId,
			conversationId,
		}: AskInputSuggestionParams) => {
			if (question.trim() === '') return;
			askNow(
				{
					content: question,
					followUpConversationId: followUpConversationId ?? null,
					conversationId: conversationId ?? null,
				},
				{
					onSuccess: (response) => {
						setQuestion('');
						onCloseModal?.();
						if (conversationId) return; // Ask more. if conversationId is provided, stay on the current conversation

						if (followUpConversationId) {
							router.push(
								`/conversations/${response.data.conversationId}?sourceConversationId=${followUpConversationId}`,
							);
						} else {
							router.push(`/conversations/${response.data.conversationId}`);
						}
					},
				},
			);
		},
		[question, askNow, router],
	);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node) &&
				question.trim() === ''
			) {
				setIsShowButton(true);
				setShowSuggestions(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [question]);

	return {
		// State
		question,
		googleSuggestions,
		followUpQuestions: followUpQuestions?.data,
		showSuggestions,
		isLoading: isFollowUpLoading,
		isAskNowLoading,
		error,
		isShowButton,
		wrapperRef,
		// Handlers
		handleAskNow,
		handleChange,
		handleGetSuggestions,
		handleSelectSuggestion,
	};
};
